import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../../utils/scrollUtils';

const InitialAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('3.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
   navigate('/courses/choking-management');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '3.1-1': 1, // Notify emergency medical services (EMS) immediately
    '3.1-2': 1, // To reassure the family and keep them informed
    '3.1-3': 2, // As soon as the incident occurs
    '3.2-1': 1, // The time and onset of choking, actions taken, and patient's response
    '3.2-2': 0, // It ensures that all necessary information is communicated in a structured and organized way
    '3.2-3': 1, // Remain available for follow-up questions or clarifications
    '3.3-1': 2, // It ensures continuity of care and patient safety
    '3.3-2': 0, // Use clear, concise language without medical jargon
    '3.3-3': 1, // Ensure communication reaches all relevant team members
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: "3.1",
      title: "3.1 Emergency Activation",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TransferWithinAStationIcon sx={{ mr: 1, color: "#81c784" }} />
            3.1 Emergency Activation
          </Typography>

          <Typography paragraph>
            Prompt activation of emergency services and communication with the
            relevant medical staff can significantly improve the patient's
            chances of survival and recovery. Effective notification ensures
            that the medical team is prepared to take over and provide advanced
            care when necessary.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <TransferWithinAStationIcon sx={{ mr: 1 }} /> Handover
              Effectiveness Analysis
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Information Retention by Handover Method
                </Typography>
                <Box
                  sx={{
                    height: 250,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Radar Chart */}
                  <Box
                    sx={{
                      width: 200,
                      height: 200,
                      position: "relative",
                      borderRadius: "50%",
                      border: "1px dashed rgba(0, 0, 0, 0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* Inner circles */}
                    <Box
                      sx={{
                        width: "75%",
                        height: "75%",
                        borderRadius: "50%",
                        border: "1px dashed rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <Box
                      sx={{
                        width: "50%",
                        height: "50%",
                        borderRadius: "50%",
                        border: "1px dashed rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <Box
                      sx={{
                        width: "25%",
                        height: "25%",
                        borderRadius: "50%",
                        border: "1px dashed rgba(0, 0, 0, 0.12)",
                      }}
                    />

                    {/* Axis lines */}
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: 1,
                        bgcolor: "rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        height: "100%",
                        width: 1,
                        bgcolor: "rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        width: "70.7%",
                        height: "70.7%",
                        transform: "rotate(45deg)",
                        transformOrigin: "center",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        width: "70.7%",
                        height: "70.7%",
                        transform: "rotate(-45deg)",
                        transformOrigin: "center",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                      }}
                    />

                    {/* Data Polygon - SBAR Method */}
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        clipPath:
                          "polygon(50% 10%, 85% 30%, 90% 70%, 50% 90%, 15% 75%, 10% 30%)",
                        bgcolor: "rgba(76, 175, 80, 0.3)",
                        border: "2px solid rgba(76, 175, 80, 0.7)",
                      }}
                    />

                    {/* Data Polygon - Verbal Only */}
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        clipPath:
                          "polygon(50% 40%, 65% 50%, 65% 65%, 50% 70%, 35% 65%, 35% 45%)",
                        bgcolor: "rgba(255, 152, 0, 0.3)",
                        border: "2px solid rgba(255, 152, 0, 0.7)",
                      }}
                    />
                  </Box>

                  {/* Labels */}
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: 15,
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    Time & Onset
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ position: "absolute", top: "30%", right: 15 }}
                  >
                    Actions Taken
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ position: "absolute", bottom: "30%", right: 15 }}
                  >
                    Patient Response
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: 15,
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    Current Status
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ position: "absolute", bottom: "30%", left: 15 }}
                  >
                    Complications
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ position: "absolute", top: "30%", left: 15 }}
                  >
                    Airway Status
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Critical Information Transfer Success Rate
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", height: 220 }}
                >
                  {/* Legend */}
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: "rgba(76, 175, 80, 0.7)",
                          mr: 0.5,
                          borderRadius: 0.5,
                        }}
                      />
                      <Typography variant="caption">SBAR Method</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: "rgba(255, 152, 0, 0.7)",
                          mr: 0.5,
                          borderRadius: 0.5,
                        }}
                      />
                      <Typography variant="caption">Verbal Only</Typography>
                    </Box>
                  </Box>

                  {/* Horizontal Bar Chart */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-around",
                    }}
                  >
                    {/* Time of incident */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ width: 120 }}>
                        Time of incident
                      </Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "94%",
                            height: 12,
                            bgcolor: "rgba(76, 175, 80, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          94%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{ width: 120 }}
                      ></Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "68%",
                            height: 12,
                            bgcolor: "rgba(255, 152, 0, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          68%
                        </Typography>
                      </Box>
                    </Box>

                    {/* Interventions */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ width: 120 }}>
                        Interventions
                      </Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "95%",
                            height: 12,
                            bgcolor: "rgba(76, 175, 80, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          95%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{ width: 120 }}
                      ></Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "72%",
                            height: 12,
                            bgcolor: "rgba(255, 152, 0, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          72%
                        </Typography>
                      </Box>
                    </Box>

                    {/* Patient response */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ width: 120 }}>
                        Patient response
                      </Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "89%",
                            height: 12,
                            bgcolor: "rgba(76, 175, 80, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          89%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{ width: 120 }}
                      ></Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "53%",
                            height: 12,
                            bgcolor: "rgba(255, 152, 0, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          53%
                        </Typography>
                      </Box>
                    </Box>

                    {/* Critical details */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="caption" sx={{ width: 120 }}>
                        Critical details
                      </Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "91%",
                            height: 12,
                            bgcolor: "rgba(76, 175, 80, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          91%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{ width: 120 }}
                      ></Typography>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: "47%",
                            height: 12,
                            bgcolor: "rgba(255, 152, 0, 0.7)",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          47%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 2,
                fontStyle: "italic",
                color: "text.secondary",
              }}
            >
              Data based on 500+ simulated handovers comparing structured SBAR
              method vs. unstructured verbal communication. The radar chart
              shows retention of different information categories, while the bar
              chart shows information transfer success rates.
            </Typography>
          </Box>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              Comprehensive Handover Information
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Time and Onset
                    </Typography>
                    <Typography variant="body2">
                      When the choking began and duration of the incident.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Actions Taken
                    </Typography>
                    <Typography variant="body2">
                      Specific interventions performed (abdominal thrusts, back
                      blows, CPR).
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Patient Response
                    </Typography>
                    <Typography variant="body2">
                      How the patient responded to each intervention.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      4
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Current Status
                    </Typography>
                    <Typography variant="body2">
                      Patient's breathing, consciousness, and overall stability.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    SBAR Technique
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Situation"
                        secondary="Current emergency and immediate concern"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Background"
                        secondary="Relevant medical history and context"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Assessment"
                        secondary="Current findings and interventions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Recommendation"
                        secondary="Suggested next steps"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Follow-Up Protocol
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stay Available"
                        secondary="Remain for additional questions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Documentation"
                        secondary="Complete accurate records"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Support"
                        secondary="Assist with additional care needs"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 3.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the most important detail to include when handing
                      over a choking patient?
                    </Typography>
                    <List dense>
                      {[
                        "The patient's insurance information",
                        "The time and onset of choking, actions taken, and patient's response",
                        "The patient's age and gender",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.2-1"] === index) {
                          if (index === correctAnswers["3.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.2-1"] &&
                          index === correctAnswers["3.2-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.2", "1", index)
                            }
                            disabled={
                              answerStatus["3.2-1"] === true &&
                              index !== correctAnswers["3.2-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.2-1"] === true &&
                                index !== correctAnswers["3.2-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.2-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          These details provide the medical team with critical
                          information to continue care appropriately.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is the SBAR technique recommended for handovers?
                    </Typography>
                    <List dense>
                      {[
                        "It ensures that all necessary information is communicated in a structured and organized way",
                        "It allows for rapid treatment decisions without the need for additional details",
                        "It prevents the need for follow-up questions",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.2-2"] === index) {
                          if (index === correctAnswers["3.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.2-2"] &&
                          index === correctAnswers["3.2-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.2", "2", index)
                            }
                            disabled={
                              answerStatus["3.2-2"] === true &&
                              index !== correctAnswers["3.2-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.2-2"] === true &&
                                index !== correctAnswers["3.2-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.2-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The SBAR format ensures that important details are
                          communicated clearly and logically, reducing the risk
                          of errors.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What should you do after handing over a patient to the
                      medical team?
                    </Typography>
                    <List dense>
                      {[
                        "Leave the area immediately to avoid crowding",
                        "Remain available for follow-up questions or clarifications",
                        "Start filling out the patient's discharge report",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.2-3"] === index) {
                          if (index === correctAnswers["3.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.2-3"] &&
                          index === correctAnswers["3.2-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.2", "3", index)
                            }
                            disabled={
                              answerStatus["3.2-3"] === true &&
                              index !== correctAnswers["3.2-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.2-3"] === true &&
                                index !== correctAnswers["3.2-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.2-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Staying available allows the medical team to confirm
                          details and ask for additional information if needed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "3.2",
      title: "3.2 Handover to Medical Team",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} /> 3.2
            Handover to Medical Team
          </Typography>

          <Typography paragraph>
            A proper handover ensures that the medical team receives all
            critical information needed to continue care effectively. A clear,
            structured report minimizes the chances of miscommunication and
            ensures the patient receives consistent and accurate treatment.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              Comprehensive Handover Information
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Time and Onset
                    </Typography>
                    <Typography variant="body2">
                      When the choking began and duration of the incident.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Actions Taken
                    </Typography>
                    <Typography variant="body2">
                      Specific interventions performed (abdominal thrusts, back
                      blows, CPR).
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Patient Response
                    </Typography>
                    <Typography variant="body2">
                      How the patient responded to each intervention.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      4
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Current Status
                    </Typography>
                    <Typography variant="body2">
                      Patient's breathing, consciousness, and overall stability.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    SBAR Technique
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Situation"
                        secondary="Current emergency and immediate concern"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Background"
                        secondary="Relevant medical history and context"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Assessment"
                        secondary="Current findings and interventions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Recommendation"
                        secondary="Suggested next steps"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Follow-Up Protocol
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stay Available"
                        secondary="Remain for additional questions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Documentation"
                        secondary="Complete accurate records"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Support"
                        secondary="Assist with additional care needs"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 3.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the most important detail to include when handing
                      over a choking patient?
                    </Typography>
                    <List dense>
                      {[
                        "The patient's insurance information",
                        "The time and onset of choking, actions taken, and patient's response",
                        "The patient's age and gender",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.2-1"] === index) {
                          if (index === correctAnswers["3.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.2-1"] &&
                          index === correctAnswers["3.2-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.2", "1", index)
                            }
                            disabled={
                              answerStatus["3.2-1"] === true &&
                              index !== correctAnswers["3.2-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.2-1"] === true &&
                                index !== correctAnswers["3.2-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.2-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          These details provide the medical team with critical
                          information to continue care appropriately.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is the SBAR technique recommended for handovers?
                    </Typography>
                    <List dense>
                      {[
                        "It ensures that all necessary information is communicated in a structured and organized way",
                        "It allows for rapid treatment decisions without the need for additional details",
                        "It prevents the need for follow-up questions",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.2-2"] === index) {
                          if (index === correctAnswers["3.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.2-2"] &&
                          index === correctAnswers["3.2-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.2", "2", index)
                            }
                            disabled={
                              answerStatus["3.2-2"] === true &&
                              index !== correctAnswers["3.2-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.2-2"] === true &&
                                index !== correctAnswers["3.2-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.2-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The SBAR format ensures that important details are
                          communicated clearly and logically, reducing the risk
                          of errors.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What should you do after handing over a patient to the
                      medical team?
                    </Typography>
                    <List dense>
                      {[
                        "Leave the area immediately to avoid crowding",
                        "Remain available for follow-up questions or clarifications",
                        "Start filling out the patient's discharge report",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.2-3"] === index) {
                          if (index === correctAnswers["3.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.2-3"] &&
                          index === correctAnswers["3.2-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.2", "3", index)
                            }
                            disabled={
                              answerStatus["3.2-3"] === true &&
                              index !== correctAnswers["3.2-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.2-3"] === true &&
                                index !== correctAnswers["3.2-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.2-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Staying available allows the medical team to confirm
                          details and ask for additional information if needed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "3.3",
      title: "3.3 Cross-team Communication",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ChatIcon sx={{ mr: 1, color: "#81c784" }} /> 3.3 Cross-team
            Communication
          </Typography>

          <Typography paragraph>
            Effective communication across different healthcare teams is crucial
            during choking incidents. Clear, concise information sharing ensures
            that all providers are aligned in their understanding of the
            patient's situation and can contribute effectively to the patient's
            care plan.
          </Typography>

          {/* Dynamic Infographic */}
          <Box
            sx={{
              my: 3,
              border: "1px solid rgba(129, 199, 132, 0.3)",
              borderRadius: 2,
              p: 2,
              bgcolor: "white",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Cross-team Communication Flow
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 300,
                bgcolor: "rgba(129, 199, 132, 0.05)",
                p: 2,
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* This would be replaced with an actual dynamic infographic component */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#81c784",
                    color: "white",
                    p: 2,
                    borderRadius: 2,
                    width: 150,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle1">First Responders</Typography>
                </Box>
                <ArrowForwardIcon sx={{ color: "#81c784" }} />
                <Box
                  sx={{
                    bgcolor: "#81c784",
                    color: "white",
                    p: 2,
                    borderRadius: 2,
                    width: 150,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle1">Emergency Team</Typography>
                </Box>
                <ArrowForwardIcon sx={{ color: "#81c784" }} />
                <Box
                  sx={{
                    bgcolor: "#81c784",
                    color: "white",
                    p: 2,
                    borderRadius: 2,
                    width: 150,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle1">Specialty Care</Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    bgcolor: "rgba(129, 199, 132, 0.2)",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" align="center">
                    Initial Assessment
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 120,
                    bgcolor: "rgba(129, 199, 132, 0.2)",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" align="center">
                    Intervention History
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 120,
                    bgcolor: "rgba(129, 199, 132, 0.2)",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" align="center">
                    Current Status
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 120,
                    bgcolor: "rgba(129, 199, 132, 0.2)",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" align="center">
                    Care Plan
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Box
                  sx={{
                    bgcolor: "rgba(129, 199, 132, 0.3)",
                    p: 2,
                    borderRadius: 2,
                    width: "80%",
                  }}
                >
                  <Typography variant="body2" align="center">
                    Continuous Feedback Loop
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <ArrowForwardIcon
                      sx={{ transform: "rotate(270deg)", color: "#81c784" }}
                    />
                    <ArrowForwardIcon
                      sx={{ transform: "rotate(90deg)", color: "#81c784" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#2e7d32", fontWeight: 600 }}
            >
              Best Practices for Cross-team Communication
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Use Clear Language
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Avoid medical jargon" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Confirm understanding" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Use standardized terms" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Communication Channels
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Use established protocols" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Ensure all teams are included" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Document communication" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Follow-up Communication
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Confirm receipt of information" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Schedule regular updates" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Address questions promptly" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 3.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is effective cross-team communication important during
                      a choking incident?
                    </Typography>
                    <List dense>
                      {[
                        "It reduces the workload for individual healthcare providers",
                        "It helps determine who is at fault if complications arise",
                        "It ensures continuity of care and patient safety",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.3-1"] === index) {
                          if (index === correctAnswers["3.3-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.3-1"] &&
                          index === correctAnswers["3.3-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.3", "1", index)
                            }
                            disabled={
                              answerStatus["3.3-1"] === true &&
                              index !== correctAnswers["3.3-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.3-1"] === true &&
                                index !== correctAnswers["3.3-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.3-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Clear communication across teams ensures that all
                          providers have the information they need to provide
                          consistent, high-quality care without gaps or
                          redundancies.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the best approach to verbal communication during a
                      choking emergency?
                    </Typography>
                    <List dense>
                      {[
                        "Use clear, concise language without medical jargon",
                        "Be as detailed as possible, including all medical terminology",
                        "Communicate only with the team leader to avoid confusion",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.3-2"] === index) {
                          if (index === correctAnswers["3.3-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.3-2"] &&
                          index === correctAnswers["3.3-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.3", "2", index)
                            }
                            disabled={
                              answerStatus["3.3-2"] === true &&
                              index !== correctAnswers["3.3-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.3-2"] === true &&
                                index !== correctAnswers["3.3-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.3-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Clear, jargon-free communication reduces the risk of
                          misunderstanding and ensures that all team members,
                          regardless of their role or expertise, can understand
                          the information.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is a critical element of communication channels
                      during a choking emergency?
                    </Typography>
                    <List dense>
                      {[
                        "Limit communication to only essential personnel",
                        "Ensure communication reaches all relevant team members",
                        "Use only written communication to avoid misunderstandings",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["3.3-3"] === index) {
                          if (index === correctAnswers["3.3-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["3.3-3"] &&
                          index === correctAnswers["3.3-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("3.3", "3", index)
                            }
                            disabled={
                              answerStatus["3.3-3"] === true &&
                              index !== correctAnswers["3.3-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["3.3-3"] === true &&
                                index !== correctAnswers["3.3-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["3.3-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Including all relevant team members in communication
                          ensures coordinated care and prevents potentially
                          harmful information gaps.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#81c784", fontWeight: 600 }}
        >
          Module 3: Notification and Communication
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Master the steps to activate emergency response, notify the medical
          team, and provide accurate patient handover.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find((section) => section.id === activeSection)?.content}

            {/* Module Buttons */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNextSection}
                  sx={{ bgcolor: "#81c784", "&:hover": { bgcolor: "#4caf50" } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InitialAssessment;
